import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BulkFileUploader from "../bulkImport/BulkFileUploader";
import ColumnMapping from "../bulkImport/ColumnMapping";
import ImportPreview from "../bulkImport/ImportPreview";
import ImportProgress from "../bulkImport/ImportProgress";
import DefaultButton from "../../buttons/defaultButton";
import { HiCheckCircle, HiExclamationCircle, HiArrowRight } from "react-icons/hi";

const IMPORT_STAGES = {
  UPLOAD: "upload",
  MAPPING: "mapping",
  PREVIEW: "preview",
  IMPORTING: "importing",
  COMPLETE: "complete",
};

const BulkImportStep = ({ onImportComplete, importJobId: existingJobId }) => {
  const navigate = useNavigate();
  const [stage, setStage] = useState(IMPORT_STAGES.UPLOAD);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [detectedColumns, setDetectedColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [validationResults, setValidationResults] = useState(null);
  const [importJobId, setImportJobId] = useState(existingJobId);
  const [importStatus, setImportStatus] = useState(null);

  // Simulate file upload and column detection
  const handleFileUpload = async (file) => {
    setUploadedFile(file);
    // Simulate API call to detect columns
    const mockDetectedColumns = [
      { name: "first_name", sample: "John" },
      { name: "last_name", sample: "Doe" },
      { name: "email", sample: "john.doe@example.com" },
      { name: "phone", sample: "+1234567890" },
      { name: "department", sample: "Engineering" },
      { name: "position", sample: "Software Engineer" },
      { name: "salary", sample: "75000" },
      { name: "start_date", sample: "2024-01-15" },
    ];
    setDetectedColumns(mockDetectedColumns);
    setStage(IMPORT_STAGES.MAPPING);
  };

  // Auto-map columns based on exact match and synonyms
  useEffect(() => {
    if (detectedColumns.length > 0 && Object.keys(columnMapping).length === 0) {
      const targetFields = [
        { key: "first_name", synonyms: ["firstname", "fname", "first name"] },
        { key: "last_name", synonyms: ["lastname", "lname", "last name", "surname"] },
        { key: "email", synonyms: ["email_address", "e-mail", "mail"] },
        { key: "phone", synonyms: ["phone_number", "mobile", "contact", "telephone"] },
        { key: "employee_id", synonyms: ["emp_id", "id", "employee number"] },
        { key: "department", synonyms: ["dept", "department_name"] },
        { key: "position", synonyms: ["job_title", "title", "role"] },
        { key: "salary", synonyms: ["pay", "compensation", "wage"] },
        { key: "start_date", synonyms: ["hire_date", "joining_date", "date_of_joining"] },
      ];

      const autoMapping = {};
      detectedColumns.forEach((col) => {
        const colName = col.name.toLowerCase().replace(/[_\s]/g, "");
        const matched = targetFields.find(
          (field) =>
            field.key === colName ||
            field.synonyms.some((syn) => syn.toLowerCase() === colName)
        );
        if (matched) {
          autoMapping[col.name] = matched.key;
        }
      });
      setColumnMapping(autoMapping);
    }
  }, [detectedColumns]);

  const handleMappingComplete = () => {
    // Simulate preview data generation
    const mockPreviewData = Array.from({ length: 5 }, (_, i) => ({
      row_number: i + 1,
      data: {
        first_name: `John${i}`,
        last_name: `Doe${i}`,
        email: `john${i}.doe@example.com`,
        phone: `+123456789${i}`,
        department: i % 2 === 0 ? "Engineering" : "Sales",
        position: "Software Engineer",
        salary: `${75000 + i * 1000}`,
        start_date: "2024-01-15",
      },
    }));
    setPreviewData(mockPreviewData);

    // Simulate validation
    const mockValidation = {
      total: 100,
      ok: 85,
      warning: 10,
      error: 5,
      rows: mockPreviewData.map((row, idx) => ({
        row_number: row.row_number,
        status: idx < 3 ? "ok" : idx === 3 ? "warning" : "error",
        errors: idx === 4 ? ["Invalid email format"] : [],
        warnings: idx === 3 ? ["Missing phone number"] : [],
      })),
    };
    setValidationResults(mockValidation);
    setStage(IMPORT_STAGES.PREVIEW);
  };

  const handleStartImport = async () => {
    // Simulate import job creation
    const jobId = `import-${Date.now()}`;
    setImportJobId(jobId);
    setStage(IMPORT_STAGES.IMPORTING);
    onImportComplete?.(jobId);

    // Simulate progress updates
    simulateImportProgress(jobId);
  };

  const simulateImportProgress = (jobId) => {
    let processed = 0;
    const total = validationResults.total;
    const interval = setInterval(() => {
      processed += Math.floor(Math.random() * 10) + 1;
      if (processed >= total) {
        processed = total;
        clearInterval(interval);
        setImportStatus({
          total,
          processed,
          imported: validationResults.ok,
          failed: validationResults.error,
          skipped: validationResults.warning,
          status: "completed",
          errors: validationResults.rows
            .filter((r) => r.status === "error")
            .map((r) => ({
              row_number: r.row_number,
              reason: r.errors.join(", "),
            })),
        });
        setStage(IMPORT_STAGES.COMPLETE);
      } else {
        setImportStatus({
          total,
          processed,
          imported: Math.floor((processed / total) * validationResults.ok),
          failed: Math.floor((processed / total) * validationResults.error),
          skipped: Math.floor((processed / total) * validationResults.warning),
          status: "processing",
        });
      }
    }, 500);
  };

  const handleReset = () => {
    setStage(IMPORT_STAGES.UPLOAD);
    setUploadedFile(null);
    setDetectedColumns([]);
    setColumnMapping({});
    setPreviewData([]);
    setValidationResults(null);
    setImportStatus(null);
  };

  return (
    <div className="flex flex-col gap-6" role="region" aria-labelledby="bulk-import-heading">
      <div>
        <h2 id="bulk-import-heading" className="text-xl font-semibold text-theme mb-2">
          Bulk Employee Import
        </h2>
        <p className="text-sm text-textLight">
          Upload a CSV or Excel file to import multiple employees at once.
        </p>
      </div>

      {/* Important Message */}
      <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <HiExclamationCircle className="text-warning-600 text-xl flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-warning-900">
              Important: Rows with errors will be skipped. Valid rows will be imported.
            </p>
            <p className="text-xs text-warning-700 mt-1">
              Review the preview carefully before starting the import.
            </p>
          </div>
        </div>
      </div>

      {/* Option to use dedicated import page */}
      <div className=" border border-primary-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-theme mb-1">
              Prefer using the dedicated import page?
            </p>
            <p className="text-xs text-textLight">
              Use the full-featured import page with advanced mapping and validation options.
            </p>
          </div>
          <DefaultButton
            variant="default"
            name="Go to Import Page"
            icon={<HiArrowRight />}
            onClick={() => navigate("/hr/staff-directory/import")}
          />
        </div>
      </div>

      {/* Stage Content */}
      {stage === IMPORT_STAGES.UPLOAD && (
        <BulkFileUploader
          onFileUpload={handleFileUpload}
          acceptedFormats={[".csv", ".xlsx", ".xls"]}
        />
      )}

      {stage === IMPORT_STAGES.MAPPING && (
        <ColumnMapping
          detectedColumns={detectedColumns}
          mapping={columnMapping}
          onMappingChange={setColumnMapping}
          onComplete={handleMappingComplete}
        />
      )}

      {stage === IMPORT_STAGES.PREVIEW && (
        <ImportPreview
          previewData={previewData}
          validationResults={validationResults}
          columnMapping={columnMapping}
          onStartImport={handleStartImport}
          onBack={() => setStage(IMPORT_STAGES.MAPPING)}
        />
      )}

      {(stage === IMPORT_STAGES.IMPORTING || stage === IMPORT_STAGES.COMPLETE) && (
        <ImportProgress
          importJobId={importJobId}
          status={importStatus}
          isComplete={stage === IMPORT_STAGES.COMPLETE}
          onReset={handleReset}
          onViewImported={() => {
            // Navigate to staff directory
            window.location.href = "/hr/staff-directory";
          }}
        />
      )}

      {/* Completion Message */}
      {stage === IMPORT_STAGES.COMPLETE && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <HiCheckCircle className="text-success-600 text-xl flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-success-900 mb-1">
                Import Completed Successfully
              </p>
              <p className="text-xs text-success-700">
                {importStatus?.imported || 0} employees imported successfully.
                {importStatus?.failed > 0 && (
                  <span> {importStatus.failed} rows failed and were skipped.</span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkImportStep;

